import { LINE_ITEM_TYPES, toWireDate, type LineItemType } from '@forecastify/shared';
import type { CalendarLabels, FilterOption, ListFilter } from '@forecastify/ui';
import type { Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────┐
 * │ The registers' shared rules — what *matching* means, how many rows a page holds, │
 * │ and what a date bound is.                                                         │
 * └───────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE HAND-BUILT TOOLBAR AND PAGER ARE GONE — ux-pass-2, 14 Oct 2026.** This file used to
 * hold `TableTools`, `usePaged` and `PagerFooter`: a `ListToolbar` and a `ListPager` bolted onto
 * raw `<table>`s on the effort and finance tabs. Every one of those tables is a `DataList` now —
 * the product owner asked for *the unified list* by name, with its multi-column header sort — so
 * the toolbar and the pager come from the list itself, and what is left here is the three rules
 * the tabs must still agree on between them.
 *
 * ⚠️ **Still paged in the browser**, and still the exception: a contract's periods, deliverables,
 * tasks and people arrive whole in one read because every tile above them is a SUM over all of
 * them. `use-row-pages.ts` pages what the filters left.
 */

/**
 * ⚠️ **Ten rows, not the product's fifty.** These registers sit under a chart and above each
 * other on one dashboard; fifty rows of deliverables would push the tasks table a screen away. The
 * pager is the settled one either way — only the page is shorter.
 */
export const REGISTER_PAGE_SIZE = 10;

export function matches(haystack: readonly (string | null)[], needle: string): boolean {
  const trimmed = needle.trim().toLocaleLowerCase();
  if (trimmed === '') return true;
  return haystack.some((one) => (one ?? '').toLocaleLowerCase().includes(trimmed));
}

/**
 * ⚠️⚠️ **A DATE INSIDE A `from..to` BOUND, WHERE `null` MEANS OPEN-ENDED.** A deliverable with no
 * end runs until the contract closes, so it is LATER than any date anybody can type: it passes
 * *ends after March* and fails *ends before March*. `contracts-screen.tsx`'s `within` drops a
 * `null` from every bounded filter, which is right for a contract with no end recorded and wrong
 * for a register where *nyitott* is a stated term.
 *
 * ⚠️ Wire dates compare as strings: `YYYY-MM-DD` sorts chronologically by construction.
 */
export function withinRange(date: string | null, range: string): boolean {
  if (range === '') return true;
  const [from = '', to = ''] = range.split('..');
  if (date === null) return to === '';
  return (from === '' || date >= from) && (to === '' || date <= to);
}

/**
 * One date-range filter in `ListToolbar`'s shape — the contracts list's two, written once for the
 * registers. ⚠️ The calendar's vocabulary comes in as a prop because `ListToolbar` cannot reach
 * `useT`; `useCalendarLabels()` already builds it.
 */
export function dateRangeFilter(
  t: Translate,
  calendar: CalendarLabels,
  filter: {
    readonly key: string;
    readonly label: string;
    readonly value: string;
    readonly onChange: (value: string) => void;
  },
): ListFilter {
  return {
    key: filter.key,
    label: filter.label,
    value: filter.value,
    onChange: filter.onChange,
    kind: 'range',
    input: 'date',
    calendar,
    today: toWireDate(new Date()),
    options: [],
    /* ⚠️ One `t()` per branch with a literal key — rule 7's shape. */
    display: (value: string) => {
      const [from = '', to = ''] = value.split('..');
      if (from !== '' && to !== '') return t('list.rangeBoth', { from, to });
      if (from !== '') return t('list.rangeFromOnly', { from });
      return t('list.rangeToOnly', { to });
    },
  };
}

/**
 * ⚠️⚠️ **FOUR TYPES, AND BOTH TABS USED TO KNOW THREE.** The effort and finance registers each
 * branched `fixed` / `time_and_material` / everything-else, so a C55 `license` deliverable was
 * labelled *Nem számlázható* on both — a licence fee that bills every month, called unbillable.
 * One `t()` per branch with a literal key, so `i18n:check` sees all four.
 */
export function lineItemTypeLabel(t: Translate, type: LineItemType): string {
  if (type === 'fixed') return t('lineItem.type.fixed');
  if (type === 'time_and_material') return t('lineItem.type.timeAndMaterial');
  if (type === 'license') return t('lineItem.type.license');
  return t('lineItem.type.nonBillable');
}

/** The billing-type filter's options — `LINE_ITEM_TYPES` itself, so a fifth type cannot be missed. */
export function lineItemTypeOptions(t: Translate): FilterOption[] {
  return LINE_ITEM_TYPES.map((type) => ({ value: type, label: lineItemTypeLabel(t, type) }));
}
